import { IconInfo16, IconMap16 } from '@warp-ds/icons/react';

import { TypingLoader } from '../../../../components/typingLoader/TypingLoader';

import type { AutocompleteOption, AutocompleteProps } from '../../../types';

import styles from '../Autocomplete.module.css';

import { DEFAULT_OPTION } from '../utils/AutocompleteDefault';

import { AutocompleteIcon } from './AutocompleteIcon';
import { AutocompleteOptionItem } from './AutocompleteOptionItem';

type DropdownAutocompleteListProps = {
    localId: string;
    visible: boolean;
    options: AutocompleteOption[];
    selected: number;
    vertical: AutocompleteProps['vertical'];
    departure?: AutocompleteProps['departure'];
    fetching: boolean;
    showLoader: boolean;
    noResults: boolean;
    showDefaultOption: boolean;
    onSelect: (o: AutocompleteOption) => void;
    onHover: (index: number) => void;
};

export const DropdownAutocompleteList = ({
    localId,
    visible,
    options,
    selected,
    vertical,
    departure,
    fetching,
    showLoader,
    noResults,
    showDefaultOption,
    onSelect,
    onHover,
}: DropdownAutocompleteListProps) => {
    if (!visible) return null;

    const isLoading = fetching && showLoader;
    const showDefault = !isLoading && showDefaultOption && !noResults && options.length === 0;
    const showNoResults = !isLoading && noResults;
    const showOptions = !isLoading && options.length > 0;

    return (
        <ul
            id={`${localId}-options`}
            data-testid={`${localId}-options`}
            className={styles['dropdown-list']}
            // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: listbox is required for combobox pattern
            role="listbox"
            aria-busy={isLoading}>
            {isLoading && (
                <li role="presentation">
                    <TypingLoader />
                    <span className="sr-only">Laster forslag…</span>
                </li>
            )}

            {showDefault && (
                <>
                    <li role="presentation">
                        <strong className={styles['dropdown-list__title']}>Utforsk</strong>
                    </li>
                    <AutocompleteOptionItem
                        id={localId}
                        index={0}
                        option={DEFAULT_OPTION}
                        selected={selected}
                        vertical={vertical}
                        departure={departure}
                        onClick={() => onSelect(DEFAULT_OPTION)}
                        onHover={() => onHover(0)}
                        icon={<IconMap16 aria-hidden focusable="false" />}
                        defaultView
                    />
                </>
            )}

            {showNoResults && (
                // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: Focus is managed on the combobox via aria-activedescendant
                <li role="option" aria-disabled="true" className="flex flex-row items-center gap-6 p-6" tabIndex={-1}>
                    <IconInfo16 aria-hidden focusable="false" />
                    <span>Fant ingen resultater...</span>
                </li>
            )}

            {showOptions &&
                options.map((option, index) => (
                    <AutocompleteOptionItem
                        key={`option-${option.id}`}
                        id={localId}
                        index={index}
                        option={option}
                        selected={selected}
                        vertical={vertical}
                        departure={departure}
                        onClick={() => onSelect(option)}
                        onHover={() => onHover(index)}
                        icon={<AutocompleteIcon option={option} vertical={vertical} departure={departure} />}
                    />
                ))}
        </ul>
    );
};
